import {
  Grid2 as Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useLocation } from "react-router-dom";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const RemoteConnections = ({
  title,
  remoteConnectionsRows,
  setManagementData,
  isSm,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [errorText, setErrorText] = useState("");
  const [statusIsError, setStatusIsError] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
        setStatusIsError(false);
      }
    }, 3500);

    return () => {
      clearInterval(clearTextInterval);
    };
  }, [errorText]);

  const rows = useMemo(() => {
    return (remoteConnectionsRows || []).map((item, index) => ({
      id: item?.id || String(index),
      email: item?.email || "",
      username: item?.username || "",
      password: "*******",
    }));
  }, [remoteConnectionsRows]);

  const refreshManagementData = async () => {
    const res = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/data/managementData`,
      { currentSession },
      { withCredentials: true },
    );

    setManagementData(res?.data?.data);
  };

  const handleAdd = async () => {
    const normalizedEmail = (email || "").trim().toLowerCase();

    if (!normalizedEmail) {
      setErrorText("Email is required.");
      setStatusIsError(true);
      return;
    }

    if (!emailRegex.test(normalizedEmail)) {
      setErrorText("Please enter a valid email.");
      setStatusIsError(true);
      return;
    }

    try {
      setAddLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remote-connections/add`,
        {
          currentSession,
          currentPath: location.pathname,
          email: normalizedEmail,
        },
        { withCredentials: true },
      );

      await refreshManagementData();

      setGeneratedData(resp?.data?.data || null);
      setPasswordModalOpen(true);
      setEmail("");
      setErrorText(resp?.data?.message || "Remote connection added.");
      setStatusIsError(false);
    } catch (e) {
      setErrorText(
        e?.response?.data?.message || "Couldn't add remote connection.",
      );
      setStatusIsError(true);
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedRow?.email) return;

    try {
      setRemoveLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remote-connections/remove`,
        {
          currentSession,
          currentPath: location.pathname,
          email: selectedRow.email,
        },
        { withCredentials: true },
      );

      await refreshManagementData();

      setErrorText(resp?.data?.message || "Remote connection removed.");
      setStatusIsError(false);
      setSelectedRow(null);
      setConfirmOpen(false);
    } catch (e) {
      setErrorText(
        e?.response?.data?.message || "Couldn't remove remote connection.",
      );
      setStatusIsError(true);
    } finally {
      setRemoveLoading(false);
    }
  };

  const columns = [
    {
      field: "email",
      headerName: "Email",
      flex: 1.4,
      minWidth: 280,
      headerClassName: "management-datagrid-title",
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      minWidth: 180,
      headerClassName: "management-datagrid-title",
    },
    {
      field: "password",
      headerName: "Password",
      flex: 0.8,
      minWidth: 150,
      headerClassName: "management-datagrid-title",
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      sortable: false,
      filterable: false,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      headerClassName: "management-datagrid-title",
      getActions: (params) => [
        <GridActionsCellItem
          key={`remove-${params.id}`}
          icon={<DeleteOutlineIcon sx={{ color: "error.main" }} />}
          label="Remove"
          disabled={removeLoading || addLoading}
          onClick={() => {
            setSelectedRow(params.row);
            setConfirmOpen(true);
          }}
        />,
      ],
    },
  ];

  return (
    <Card sx={{ minHeight: "35rem" }}>
      <CardHeader title={title} />
      <CardContent>
        <DataGrid
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            minHeight: "24rem",
          }}
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableRowSelectionOnClick
        />

        <Grid
          container
          spacing={isSm ? 1 : 2}
          alignItems="center"
          sx={{ mt: 1 }}
        >
          <Grid size={{ xs: 12, md: 9 }}>
            <TextField
              label="Email"
              variant="filled"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={addLoading || removeLoading}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <LoadingButton
              loading={addLoading}
              disabled={!email || addLoading || removeLoading}
              fullWidth
              variant="contained"
              onClick={handleAdd}
            >
              Add
            </LoadingButton>
          </Grid>
        </Grid>

        <Typography
          variant="body1"
          color={statusIsError ? "error" : "primary"}
          fontWeight="bold"
          textAlign="center"
          sx={{ mt: "1rem", minHeight: "1.5rem" }}
        >
          {errorText}
        </Typography>

        <Dialog
          open={confirmOpen}
          onClose={removeLoading ? () => {} : () => setConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Remove</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove remote connection for{" "}
              <strong>{selectedRow?.email}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmOpen(false)}
              disabled={removeLoading}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleRemove}
              loading={removeLoading}
              variant="contained"
              color="error"
            >
              Remove
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={passwordModalOpen}
          onClose={() => {}}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle color={"primary"}>Remote Connection Created</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1 }}>
              Email: <strong>{generatedData?.email}</strong>
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Username: <strong>{generatedData?.username}</strong>
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Generated Password:{" "}
              <strong>{generatedData?.generated_password}</strong>
            </Typography>
            <Typography color="error" fontWeight="bold">
              This password will be shown only once and cannot be displayed
              again for security reasons.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPasswordModalOpen(false)}
              variant="contained"
            >
              I Understand
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
